import React, { useRef, useEffect } from "react";
import { PositionType, PositionTypeString, getFixedClockDecubitusPositionForDate, getDecubitusPositionColor } from "app/models/position";
import p5 from "p5";
import "./decubitus-clock-drawing.scss";

// Props
interface DynamicDecubitusClockDrawingProps {
    startMonitoringDate: Date;
    allHourLabels?: boolean;
    size?: number;
    hasBlinkingBall?: boolean;
}

// Component
function DynamicDecubitusClockDrawing({ startMonitoringDate, allHourLabels = true, size = 400, hasBlinkingBall = true }: DynamicDecubitusClockDrawingProps) {
    const sketchRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const sketch = (p: p5) => {
            // Function to calculate the initial angle based on the startMonitoringDate
            const calculateInitialAngle = (date: Date) => {
                const hour = date.getHours();
                const minute = date.getMinutes();
                return (hour % 12) * 30 + (minute / 60) * 30 - 90;
            };

            const initialAngle = calculateInitialAngle(startMonitoringDate);
            const initialPosition = getFixedClockDecubitusPositionForDate(startMonitoringDate);

            // Rotate the labels array based on the initial position
            let labels: PositionTypeString[] = [
                PositionType.DLD,
                PositionType.DDH,
                PositionType.DLE,
                PositionType.DLD,
                PositionType.DDH,
                PositionType.DLE,
            ];

            // Find the index of the initial position and rotate the labels array
            const initialIndex = labels.indexOf(initialPosition);
            labels = labels.slice(initialIndex).concat(labels.slice(0, initialIndex));

            p.setup = () => {
                p.createCanvas(size, size);
                p.angleMode(p.DEGREES);
                p.noLoop();
                p.frameRate(1);
            };

            p.draw = () => {
                p.clear();
                p.translate(p.width / 2, p.height / 2);

                const radius = size * 0.75;

                // Segments
                p.noStroke();
                for (let i = 0; i < 12; i += 2) {
                    const color = getDecubitusPositionColor(labels[i / 2]);
                    p.fill(color);
                    let startAngle = initialAngle + p.map(i, 0, 12, 0, 360);
                    let endAngle = initialAngle + p.map(i + 2, 0, 12, 0, 360);
                    p.arc(0, 0, radius, radius, startAngle, endAngle, p.PIE);

                    // Labels
                    let middleAngle = (startAngle + endAngle) / 2;
                    let labelX = (radius * 0.3) * p.cos(middleAngle);
                    let labelY = (radius * 0.3) * p.sin(middleAngle);
                    p.textAlign(p.CENTER, p.CENTER);
                    p.textSize(size * 0.06);
                    p.textStyle(p.BOLD);
                    p.fill(34, 61, 23);
                    p.text(labels[i / 2], labelX, labelY);
                    p.textStyle(p.NORMAL);
                }

                // Markers and hour labels
                p.strokeWeight(size * 0.005);
                p.stroke(0);
                const increment = allHourLabels ? 1 : 2;
                for (let i = 0; i < 12; i += increment) {
                    let angle = p.map(i, 0, 12, 0, 360);
                    let x1 = (radius * 0.4) * p.cos(angle - 90);
                    let y1 = (radius * 0.4) * p.sin(angle - 90);
                    let x2 = (radius * 0.46) * p.cos(angle - 90);
                    let y2 = (radius * 0.46) * p.sin(angle - 90);
                    p.line(x1, y1, x2, y2);

                    // Hour labels
                    let hourLabelX = (radius * 0.53) * p.cos(angle - 90);
                    let hourLabelY = (radius * 0.53) * p.sin(angle - 90);
                    let hourLabelText = i;
                    if (i === 0) {
                        hourLabelText = 12;
                    }
                    if (p.hour() > 12) {
                        hourLabelText = (hourLabelText + 12) % 24;
                    }
                    p.textAlign(p.CENTER, p.CENTER);
                    p.textSize(size * 0.04);
                    p.fill(0);
                    p.text(hourLabelText, hourLabelX, hourLabelY);
                }

                // Hour hand
                let h = p.hour() % 12;
                let m = p.minute();
                let hourAngle = p.map(h + m / 60, 0, 12, -90, 270);
                p.stroke(200, 20, 0);
                p.strokeWeight(size * 0.02);
                p.line(0, 0, (radius * 0.27) * p.cos(hourAngle), (radius * 0.27) * p.sin(hourAngle));

                // Display current time as text (hh:mm) below the clock
                p.textAlign(p.CENTER, p.CENTER);
                p.textSize(size * 0.04);
                p.noStroke();
                p.fill(0);
                const formattedTime = () => {
                    const hour = p.hour();
                    const minute = p.minute();
                    return `${hour < 10 ? "0" + hour : hour}:${minute < 10 ? "0" + minute : minute}`;
                };
                p.text(formattedTime(), 0, radius * 0.63); // Adjusted to display below the clock
            };
        };

        const p5Instance = new p5(sketch, sketchRef.current!);

        const interval = setInterval(() => {
            p5Instance.redraw();
        }, 60000); // Update every minute

        return () => {
            p5Instance.remove();
            clearInterval(interval);
        };
    }, [startMonitoringDate, allHourLabels, size]);

    return <div ref={sketchRef} style={{ display: "flex", justifyContent: "center", position: "relative" }}>
        {hasBlinkingBall &&
            <div className="blinking-ball">
                <div className="blinking-ball-text">
                    {/* Em Tempo Real */}
                </div>
            </div>
        }
    </div>;
}

export default DynamicDecubitusClockDrawing;
